import { ReactElement, useEffect, useMemo, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { APP_LAYOUT_HEIGHT } from '@/config';
import { SettingsSidebar } from '@/features/settings/components';
import { useUserInfo } from '@/features/users';
import { useTitle } from '@/hooks';

import { AppHeader, AppSidebar } from '../app';

const SIDEBAR_EXPANDED_WIDTH = 280;

const Content = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexBasis: `calc(100vh - ${APP_LAYOUT_HEIGHT.HEADER}px)`,
  backgroundColor: '#F9F9FA',
  overflow: 'auto',
  marginLeft: `${SIDEBAR_EXPANDED_WIDTH}px`,
});

export const SettingsLayout = (): ReactElement => {
  useTitle('Settings');

  const location = useLocation();
  const [selectedPathName, setSelectedPathName] = useState('');
  const { user_type } = useUserInfo();

  const isAdmin = user_type !== 'standard'; // Example value – replace with actual context value

  const routes = useMemo(
    () => [
      {
        header: {
          name: 'General Settings',
        },
        subRoutes: [
          {
            path: '/user-profile',
            name: 'My Profile',
            disabled: false, // Always enabled
          },
          {
            path: '/company-profile',
            name: 'Company Profile',
            disabled: !isAdmin, // Disable if not admin
          },
          {
            path: '/users',
            name: 'Users',
            disabled: !isAdmin,
          },
          {
            path: '/subscriptions',
            name: 'Subscriptions',
            disabled: !isAdmin,
          },
          {
            path: '/global-letter-settings',
            name: 'Global Letter Settings',
            disabled: !isAdmin,
          },
          {
            path: '/integrations',
            name: 'Integrations',
            disabled: !isAdmin,
          },
        ],
      },
      {
        header: {
          name: 'Application Settings',
        },
        subRoutes: [
          {
            path: '/engage',
            name: 'Engage',
            disabled: !isAdmin,
          },
          {
            path: '/actions',
            name: 'Actions',
            disabled: !isAdmin,
          },
          // {
          //   path: '/blueprint',
          //   name: 'Blueprint',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/business-structure-advice',
          //   name: 'Business Structure Advice',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/letters-merge',
          //   name: 'Letters Merge',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/progress',
          //   name: 'Progress',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/protect',
          //   name: 'Protect',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/service-plan',
          //   name: 'Service Plan',
          //   disabled: !isAdmin,
          // },
          // {
          //   path: '/trust-distribution',
          //   name: 'Trust Distribution',
          //   disabled: !isAdmin,
          // },
        ],
      },
    ],
    [isAdmin],
  );

  useEffect(() => {
    interface Route {
      path: string;
      name: string;
    }

    interface Header {
      name: string;
    }

    interface RouteGroup {
      header: Header;
      subRoutes: Route[];
    }

    const findPathName = (routes: RouteGroup[], pathname: string): string => {
      for (const route of routes) {
        for (const subRoute of route.subRoutes) {
          if (pathname.startsWith(`/settings${subRoute.path}`)) {
            return subRoute.name;
          }
        }
      }
      return 'Settings';
    };
    setSelectedPathName(findPathName(routes, location.pathname));
  }, [location.pathname, routes]);

  // If the user is not an admin and tries to access settings (except profile), redirect to profile
  if (
    (!isAdmin && location.pathname !== '/settings/user-profile') ||
    location.pathname === '/settings'
  ) {
    return <Navigate to="/settings/user-profile" replace />;
  }

  const showSidebar = isAdmin || location.pathname === '/settings/user-profile';

  return (
    <Stack height="100vh" direction="row">
      {showSidebar && <AppSidebar />}
      <Stack
        width={`calc(100% - ${showSidebar ? SIDEBAR_EXPANDED_WIDTH : 0}px)`}
        flexGrow={1}
        flexShrink={1}
      >
        <AppHeader />
        {showSidebar && <SettingsSidebar routes={routes} />}
        <Content>
          <Box sx={{ padding: '20px' }}>
            <Box>
              <Typography variant="bodySmall">Settings</Typography>
            </Box>
            <Box>
              <Typography variant="h6">{selectedPathName}</Typography>
            </Box>
          </Box>
          <Outlet />
        </Content>
      </Stack>
    </Stack>
  );
};
